import React from "react";
import { ThreeDots } from "react-loader-spinner";
import Checkbox from "@mui/material/Checkbox";
import { useAuth } from "react-oidc-context";
import styles from "../QuoteMultipleDestinationsPanel.module.css";
import { SuccessCheckIcon } from "../../../icons/SuccessCheckIcon";
import {
  FreightRail,
  IDestinationExtension,
  IQuoteExtensionOld,
  IQuoteExtension,
} from "../../../types/ICompAnalysisQMD";
import { useNitroStore } from "../../../store/Store";
import NitroService from "../../../utils/nitro.service";
import {
  hasProductForQuote,
  getMissingOriginRowKey,
  MissingRailFreightDesktopSections,
  calculateStations,
} from "../components/multiLocationQuoteUtils";
import { IOccupant } from "../../../types/ILocation";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import { icons } from "../../../svg";
import ClockIcon from "../../../icons/ClockIcon";
import { RoundedChecked } from "../../../icons";
import NitroRequestService from "../../../utils/nitroRequest.service";
import type {
  TariffRowStatus,
  PendingTariffPollRequest,
} from "../../../store/CompetitiveAnalysisStore";
import { useStickyTableHeader } from "../../../hooks/useStickyTableHeader";

// TODO: number of locatiouns that are in table is wrong but might be due to faking data
interface RailMissingOriginsTableProps {
  rows: TableRow[];
  loading: boolean;
  destination: IDestinationExtension;
}

type RowProductStatus = "check" | "add" | "dash";
type RowRateStatus = "request" | "requested" | "dash";
// TODO: why is this exported twice and just use the type lol
type RowTariffStatus = TariffRowStatus;

export interface TableRow {
  rowKey: string;
  originName: string;
  productStatus?: RowProductStatus;
  rateStatus: RowRateStatus;
  tariffStatus: RowTariffStatus;
  publicTariffRate?: string;
  quote: IQuoteExtensionOld | IQuoteExtension;
}

export const buildRows = (
  sections: MissingRailFreightDesktopSections,
  destinationId: string,
  product?: string,
): TableRow[] => {
  const rows: TableRow[] = [];

  sections.missingProductOrigins?.forEach((q) => {
    rows.push({
      rowKey: getMissingOriginRowKey(destinationId, q),
      originName: q.origin.name,
      productStatus:
        product && hasProductForQuote(q, product) ? "check" : "add",
      rateStatus: "dash",
      tariffStatus: "dash",
      quote: q,
    });
  });

  sections.originsNotRequested?.forEach((q) => {
    rows.push({
      rowKey: getMissingOriginRowKey(destinationId, q),
      originName: q.origin.name,
      productStatus:
        product && hasProductForQuote(q, product) ? "check" : "add",
      rateStatus: "request",
      tariffStatus: "dash",
      quote: q,
    });
  });

  sections.originsWithRateNotFound?.forEach((q) => {
    rows.push({
      rowKey: getMissingOriginRowKey(destinationId, q),
      originName: q.origin.name,
      productStatus:
        product && hasProductForQuote(q, product) ? "check" : "add",
      rateStatus: "requested",
      tariffStatus: "not found",
      quote: q,
    });
  });

  sections.originsInProgress?.forEach((q) => {
    rows.push({
      rowKey: getMissingOriginRowKey(destinationId, q),
      originName: q.origin.name,
      productStatus:
        product && hasProductForQuote(q, product) ? "check" : "add",
      rateStatus: "requested",
      tariffStatus: "check",
      quote: q,
    });
  });

  sections.originsWithPublicRate?.forEach((q) => {
    rows.push({
      rowKey: getMissingOriginRowKey(destinationId, q),
      originName: q.origin.name,
      productStatus:
        product && hasProductForQuote(q, product) ? "check" : "add",
      rateStatus: "requested",
      tariffStatus: "found",
      publicTariffRate: String(
        (q.freight as FreightRail).totalFreight.toFixed(2),
      ),
      quote: q,
    });
  });

  return rows;
};

export const createLinkToLocation = (
  originalUrl: string,
  locationName: string,
): string => {
  const url = new URL(originalUrl);
  const params = url.searchParams;

  params.delete("location");
  params.delete("city");
  params.delete("party");
  params.append("location", locationName.trim());
  url.search = params.toString();

  return url.toString();
};

export const RailMissingOriginsTableOld: React.FC<
  RailMissingOriginsTableProps
> = ({ rows, loading, destination }) => {
  const [product, setMultiLocationDestinations, products, featureFlags] =
    useNitroStore((state) => [
      state.product,
      state.setMultiLocationDestinations,
      state.products,
      state.featureFlags,
    ]);
  const { multiRailStation } = featureFlags;
  const [
    triggerRefetchMultiLocation,
    setExpandedDestination,
    setOriginToTroubleshoot,
    setDestinationToTroubleshoot,
    setDisplayAddProductToOriginPanel,
    setDisplayRailRateRequestPanel,
    setRailRateRequestContext,
    setShowRailRateRequestFailedPopup,
    requestedRailRateRows,
    addRequestedRailRateRow,
    tariffStateByRowKey,
    exitRequestMultipleModeTrigger,
    enqueueTariffPollRequests,
  ] = useNitroCAStore((state) => [
    state.triggerRefetchMultiLocation,
    state.setExpandedDestination,
    state.setOriginToTroubleshoot,
    state.setDestinationToTroubleshoot,
    state.setDisplayAddProductToOriginPanel,
    state.setDisplayRailRateRequestPanel,
    state.setRailRateRequestContext,
    state.setShowRailRateRequestFailedPopup,
    state.requestedRailRateRows,
    state.addRequestedRailRateRow,
    state.tariffStateByRowKey,
    state.exitRequestMultipleModeTrigger,
    state.enqueueTariffPollRequests,
  ]);
  const auth = useAuth();
  const { ref: shellRef, isSticky } = useStickyTableHeader(
    "deskstop-destination-table",
  );
  const [loadingProductRows, setLoadingProductRows] = React.useState<
    Set<string>
  >(new Set());
  const [loadingRateRows, setLoadingRateRows] = React.useState<Set<string>>(
    new Set(),
  );
  const [requestMultipleMode, setRequestMultipleMode] = React.useState(false);
  const [selectedRows, setSelectedRows] = React.useState<Set<string>>(
    new Set(),
  );

  const toggleSelectedRow = (rowKey: string) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  };

  const exitRequestMultipleMode = () => {
    setRequestMultipleMode(false);
    setSelectedRows(new Set());
  };

  React.useEffect(() => {
    if (exitRequestMultipleModeTrigger > 0) {
      exitRequestMultipleMode();
    }
  }, [exitRequestMultipleModeTrigger]);

  // Origins with both a Koch and competitor occupant only show the Koch row (see
  // sortedRows dedupe below), so requesting the Koch rate must also silently poll
  // for the competitor rate the backend returns alongside it.
  const findHiddenCompetitorSiblings = (
    originId: string,
    excludeRowKeys: Set<string>,
  ) =>
    rows.filter(
      (r) =>
        r.quote.competitor &&
        r.quote.origin.id === originId &&
        r.rateStatus === "request" &&
        !requestedRailRateRows.has(r.rowKey) &&
        !excludeRowKeys.has(r.rowKey),
    );

  const kickOffRailRateRequest = async (tableRows: TableRow[]) => {
    try {
      const result = await NitroRequestService.postRequestForRailRate({
        origins: tableRows.map(({ quote }) => ({
          origin: {
            name: quote.origin.name,
            city: `${quote.origin.details?.city}, ${quote.origin.details?.state}`,
            address: quote.origin.details?.address || "",
            zipCode: quote.origin.details?.zipCode || "",
            stations: calculateStations(multiRailStation, quote.origin.details),
            state: quote.origin.details?.state || "",
            link: createLinkToLocation(window.location.href, quote.origin.name),
          },
          competitor: quote.competitor,
        })),
        destination: {
          name: destination.name,
          city: `${destination.details?.city}, ${destination.details?.state}`,
          address: destination.details?.address || "",
          zipCode: destination.details?.zipCode || "",
          stations: calculateStations(multiRailStation, destination.details),
          state: destination.details?.state || "",
          link: createLinkToLocation(window.location.href, destination.name),
        },
        rateType: "Tariffs Live",
        stcc: String(products.get(product)?.stccCodes[0]) || "",
        carType: products.get(product)?.carType || "",
        requestedBy: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
        product,
      });

      if (result.status < 200 || result.status >= 300) {
        // TODO: add user-facing error handling
        // TODO: Submit takes a long time and need to make user journey better
        console.error("postRequestForRailRate failed", result.status, result);
        setShowRailRateRequestFailedPopup(true);
        return;
      }
      enqueueTariffPollRequests(
        tableRows.map(
          (row): PendingTariffPollRequest => ({
            rowKey: row.rowKey,
            quote: row.quote,
            destination,
          }),
        ),
      );
      addRequestedRailRateRow(tableRows.map((r) => r.rowKey));
    } catch (error) {
      console.error("postRequestForRailRate failed", error);
      setShowRailRateRequestFailedPopup(true);
    }
  };

  const requestableRailRateRowCount = rows.filter(
    (r) => r.rateStatus === "request" && !requestedRailRateRows.has(r.rowKey),
  ).length;

  const allSelectedAreCompetitors =
    selectedRows.size > 0 &&
    rows
      .filter((r) => selectedRows.has(r.rowKey))
      .every((r) => r.quote.competitor);

  const addProductToOccupant = async (
    occupant: IOccupant,
    locationId: string,
  ) => {
    const updatedOccupant = {
      ...occupant,
      products: [...occupant.products, { product, quantity: 0 }],
    } as IOccupant;
    await NitroService.updateOccupants({
      id: locationId,
      occupants: [updatedOccupant],
    });
  };

  const renderProductCell = (row: TableRow) => {
    const { quote, rowKey } = row;
    const status: RowProductStatus = hasProductForQuote(quote, product)
      ? "check"
      : "add";
    if (status === "check") return <SuccessCheckIcon />;
    if (status === "add") {
      if (loadingProductRows.has(rowKey)) {
        return <ThreeDots height="20" width="30" color="#4fa94d" />;
      }
      return (
        <button
          type="button"
          className={styles.railMissingOriginsPill}
          onClick={async () => {
            if (quote.origin.details!.occupants.length === 1) {
              setLoadingProductRows((prev) => new Set([...prev, rowKey]));
              await addProductToOccupant(
                quote.origin.details!.occupants[0],
                quote.origin.id,
              );
              setMultiLocationDestinations([]);
              triggerRefetchMultiLocation();
              setExpandedDestination(
                { id: destination.id, name: destination.name },
                { expandRailWithNoFreight: true },
              );
            } else {
              setDestinationToTroubleshoot(destination);
              setOriginToTroubleshoot(quote.origin, !quote.competitor);
              setDisplayAddProductToOriginPanel(true);
            }
          }}
          aria-label={`Add ${product}`}
        >
          <span className={styles.railMissingOriginsPillPlus}>+</span>
          <span>Add</span>
        </button>
      );
    }
    return <span className={styles.railMissingOriginsDash}>—</span>;
  };

  const renderRailRateCell = (row: TableRow) => {
    const { rateStatus: status, quote, rowKey } = row;
    const effectiveStatus = requestedRailRateRows.has(rowKey)
      ? "requested"
      : status;
    if (
      requestMultipleMode &&
      effectiveStatus === "request" &&
      !requestedRailRateRows.has(rowKey)
    ) {
      return (
        <Checkbox
          disableRipple
          checked={selectedRows.has(rowKey)}
          onChange={() => toggleSelectedRow(rowKey)}
          icon={<span />}
          checkedIcon={<RoundedChecked color="#EA580C" />}
          sx={{
            width: "20px",
            height: "20px",
            flexShrink: 0,
            borderRadius: "8px",
            border: "2px solid #FED7AA",
            background: "#FFF",
            padding: 0,
            "&.Mui-checked": {
              color: "#EA580C",
              border: "none",
            },
          }}
          aria-label="Select rail rate request row"
        />
      );
    }

    if (effectiveStatus === "requested") {
      return (
        <span className={styles.railMissingOriginsRequestedPill}>
          <ClockIcon className={styles.railMissingOriginsRequestedPillIcon} />
          <span>Requested</span>
        </span>
      );
    }
    if (effectiveStatus === "request") {
      if (loadingRateRows.has(rowKey)) {
        return <ThreeDots height="20" width="30" color="#4fa94d" />;
      }

      return (
        <button
          type="button"
          className={styles.railMissingOriginsPill}
          onClick={async () => {
            if (quote.competitor) {
              setLoadingRateRows((prev) => new Set([...prev, rowKey]));
              try {
                await kickOffRailRateRequest([row]);
              } finally {
                setLoadingRateRows((prev) => {
                  const next = new Set(prev);
                  next.delete(rowKey);
                  return next;
                });
              }

              return;
            }
            const siblings = findHiddenCompetitorSiblings(
              quote.origin.id,
              new Set([rowKey]),
            );
            setRailRateRequestContext({
              rows: [
                { rowKey, quote },
                ...siblings.map((r) => ({
                  rowKey: r.rowKey,
                  quote: r.quote,
                  silent: true,
                })),
              ],
              destination,
            });
            setDisplayRailRateRequestPanel(true);
          }}
          aria-label="Request Rail Rate"
        >
          <span className={styles.railMissingOriginsPillPlus}>+</span>
          <span>Request</span>
        </button>
      );
    }
    return <span className={styles.railMissingOriginsDash}>—</span>;
  };

  const renderPublicTariffRateCell = (row: TableRow) => {
    const override = tariffStateByRowKey[row.rowKey];
    const status = override?.status ?? row.tariffStatus;
    const rate = override?.rate ?? row.publicTariffRate;

    if (status === "found") {
      return (
        <span className={styles.railMissingOriginsPublicTariffBadge}>
          ${rate}
        </span>
      );
    }
    if (status === "checking") {
      const displaySeconds =
        tariffStateByRowKey[row.rowKey]?.secondsRemaining ?? 60;
      return (
        <span className={styles.tariffCountdown}>
          <span className={styles.tariffCountdownPrimary}>
            Looking up tariff rate...
          </span>
          <span className={styles.tariffCountdownSecondary}>
            -{displaySeconds}s remaining
          </span>
        </span>
      );
    }
    if (status === "not found") {
      return "Not Found";
    }
    if (status === "try again") {
      return (
        <button
          type="button"
          className={styles.truckMissingOriginsPill}
          onClick={() =>
            enqueueTariffPollRequests([
              { rowKey: row.rowKey, quote: row.quote, destination },
            ])
          }
          aria-label={`Check again for public tariff rate for ${row.originName}`}
        >
          <span className={styles.truckMissingOriginsPillPlus}>+</span>
          <span>Check Again</span>
        </button>
      );
    }
    if (status === "check") {
      return (
        <button
          type="button"
          className={styles.truckMissingOriginsPill}
          onClick={() =>
            enqueueTariffPollRequests([
              { rowKey: row.rowKey, quote: row.quote, destination },
            ])
          }
          aria-label={`Check again for public tariff rate for ${row.originName}`}
        >
          <span className={styles.truckMissingOriginsPillPlus}>+</span>
          <span>Check</span>
        </button>
      );
    }
    return <span className={styles.railMissingOriginsDash}>—</span>;
  };

  const sortedRows = React.useMemo(() => {
    // Deduplicate competitor rows that match Koch origin rows
    const kochOriginNames = new Set(
      rows.filter((r) => !r.quote.competitor).map((r) => r.originName),
    );
    const dedupedRows = rows.filter(
      (r) => !r.quote.competitor || !kochOriginNames.has(r.originName),
    );

    // Assign a priority to each row for sorting purposes
    const getRowPriority = (row: TableRow) => {
      const effectiveRateStatus = requestedRailRateRows.has(row.rowKey)
        ? "requested"
        : row.rateStatus;

      // 1. Rate already requested
      if (effectiveRateStatus === "requested") return 1;

      // 2. User still needs to request rate
      if (effectiveRateStatus === "request") return 2;

      // 3. Everything else
      return 3;
    };
    // Sort the deduplicated rows based on their priority and maintain stable order within each priority group
    return dedupedRows
      .map((row, index) => ({ row, index })) // preserve original order within each group
      .sort((a, b) => {
        const priorityDiff = getRowPriority(a.row) - getRowPriority(b.row);
        if (priorityDiff !== 0) return priorityDiff;

        // stable fallback so rows within the same bucket stay in original order
        return a.index - b.index;
      })
      .map(({ row }) => row);
  }, [rows, requestedRailRateRows]);

  return (
    <div
      id="rail-missing-origins-table"
      ref={shellRef}
      className={`${styles.railMissingOriginsTableShell} ${styles.missingOriginsLegacy}`}
      style={{ borderRadius: isSticky ? "0 0 14px 14px" : "14px" }}
    >
      {/* Table header row */}
      <div className={styles.railMissingOriginsTableHeader}>
        <div className={styles.railMissingOriginsColOrigin}>ORIGIN</div>
        <div className={styles.railMissingOriginsColUan}>{product}</div>
        <div className={styles.railMissingOriginsColRailRate}>
          RAIL RATE
          {(requestableRailRateRowCount > 1 || requestMultipleMode) && (
            <button
              type="button"
              className={styles.railRateRequestMultipleButton}
              onClick={() => {
                if (requestMultipleMode) {
                  exitRequestMultipleMode();
                } else {
                  setRequestMultipleMode(true);
                }
              }}
            >
              {requestMultipleMode ? "Cancel" : "+ Request Multiple"}
            </button>
          )}
        </div>
        <div className={styles.railMissingOriginsColPublicTariffRate}>
          PUBLIC TARIFF RATE
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className={styles.railMissingOriginsRow}>
          <span className={styles.railMissingOriginsDash}>Loading…</span>
        </div>
      )}

      {/* Data rows */}
      {!loading &&
        sortedRows.map((row) => (
          <div key={row.rowKey} className={styles.railMissingOriginsRow}>
            {/* Origin */}
            <div className={styles.railMissingOriginsColOrigin}>
              <div className={styles.railMissingOriginsCityCell}>
                <img
                  src={
                    row.quote.competitor ? icons.RedDotIcon : icons.BlueDotIcon
                  }
                  alt="Dot Icon"
                  className={styles.dotIcon}
                />
                <span
                  id={`rail-missing-origin-name-${row.rowKey}`}
                  className={`${styles.originName} ${
                    row.quote.competitor
                      ? styles.originNameRed
                      : styles.originNameBlue
                  }`}
                >
                  {row.originName}
                </span>
              </div>
            </div>
            <div className={styles.railMissingOriginsColUan}>
              {renderProductCell(row)}
            </div>

            {/* Rail Rate */}
            <div className={styles.railMissingOriginsColRailRate}>
              {renderRailRateCell(row)}
            </div>

            {/* Public Tariff Rate */}
            <div className={styles.railMissingOriginsColPublicTariffRate}>
              {renderPublicTariffRateCell(row)}
            </div>
          </div>
        ))}

      {/* Action bar — shown as the last row in requestMultipleMode */}
      {requestMultipleMode && (
        <div className={styles.railRateRequestMultipleActionBar}>
          <span className={styles.railRateRequestMultipleActionBarLabel}>
            {selectedRows.size} origin{selectedRows.size !== 1 ? "s" : ""}{" "}
            selected
          </span>
          <div className={styles.railRateRequestMultipleActionBarButtons}>
            <button
              type="button"
              className={styles.railRateRequestMultipleActionBarCancel}
              onClick={exitRequestMultipleMode}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.railRateRequestMultipleActionBarNext}
              disabled={selectedRows.size === 0}
              onClick={async () => {
                const selectedRowData = rows.filter((r) =>
                  selectedRows.has(r.rowKey),
                );
                const hasKochQuote = selectedRowData.some(
                  (r) => !r.quote.competitor,
                );
                if (hasKochQuote) {
                  const selectedRowKeys = new Set(
                    selectedRowData.map((r) => r.rowKey),
                  );
                  const siblingsByRowKey = new Map<
                    string,
                    { rowKey: string; quote: TableRow["quote"] }
                  >();
                  selectedRowData
                    .filter((r) => !r.quote.competitor)
                    .forEach((r) => {
                      findHiddenCompetitorSiblings(
                        r.quote.origin.id,
                        selectedRowKeys,
                      ).forEach((sibling) => {
                        siblingsByRowKey.set(sibling.rowKey, {
                          rowKey: sibling.rowKey,
                          quote: sibling.quote,
                        });
                      });
                    });
                  setRailRateRequestContext({
                    rows: [
                      ...selectedRowData.map((r) => ({
                        rowKey: r.rowKey,
                        quote: r.quote,
                      })),
                      ...Array.from(siblingsByRowKey.values()).map(
                        (sibling) => ({ ...sibling, silent: true }),
                      ),
                    ],
                    destination,
                  });
                  setDisplayRailRateRequestPanel(true);
                  return;
                }
                const selectedRowKeys = selectedRowData.map((r) => r.rowKey);
                setLoadingRateRows(
                  (prev) => new Set([...prev, ...selectedRowKeys]),
                );
                exitRequestMultipleMode();
                try {
                  await kickOffRailRateRequest(selectedRowData);
                } finally {
                  setLoadingRateRows((prev) => {
                    const next = new Set(prev);
                    selectedRowKeys.forEach((key) => next.delete(key));
                    return next;
                  });
                }
              }}
            >
              {allSelectedAreCompetitors ? (
                "Submit"
              ) : (
                <>
                  Next
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M3 7H11M11 7L7.5 3.5M11 7L7.5 10.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
